import { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";

import { QUERY_WIDTHS } from "@utils/index";
import { useCommonStore, useUserStore } from "@stores/index";
import {
  useUserConnectRedStatement,
  useUserDisconnectRedStatement,
} from "@providers/index";
import { useNotify } from "@hooks/index";
import {
  MobileHeader,
  Button,
  ConfirmModal,
  BusinessSelector,
  Modal,
  Loader,
} from "@components/index";

export const RedStatementPage = () => {
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });
  const setCommonState = useCommonStore(({ setState }) => setState);
  const redConnectedBusinessId = useUserStore(
    ({ redConnectedBusinessId }) => redConnectedBusinessId,
  );
  const clientCode = useUserStore(({ clientCode }) => clientCode);
  const businesses = useUserStore(({ businesses }) => businesses);
  const setUserState = useUserStore(({ setState }) => setState);
  const notify = useNotify();

  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [showNoClientCodeModal, setShowNoClientCodeModal] = useState(false);
  const [selectedBusinessId, setSelectedBusinessId] = useState("");
  const [isInitiated, setIsInitiated] = useState(false);

  const { mutate: mutateConnect, isPending: isConnecting } =
    useUserConnectRedStatement(
      () => {
        setUserState({ redConnectedBusinessId: selectedBusinessId });
        setShowConnectModal(false);
        setSelectedBusinessId(selectedBusinessId);
        notify("success", "RED Statement connected successfully.");
      },
      ({ message }) => {
        notify("error", message);
      },
    );

  const { mutate: mutateDisconnect, isPending: isDisconnecting } =
    useUserDisconnectRedStatement(
      () => {
        setUserState({ redConnectedBusinessId: "" });
        setSelectedBusinessId("");
        notify("success", "RED Statement disconnected successfully.");
      },
      ({ message }) => {
        notify("error", message);
      },
    );

  useEffect(() => {
    if (redConnectedBusinessId !== null) {
      setSelectedBusinessId(redConnectedBusinessId);
      setIsInitiated(true);
    }
  }, [redConnectedBusinessId]);

  useEffect(() => {
    setCommonState({ isLoading: isConnecting || isDisconnecting });
  }, [isConnecting, isDisconnecting, setCommonState]);

  const handleOpenModal = () => {
    if (!clientCode) {
      setShowNoClientCodeModal(true);
    } else {
      setShowConnectModal(true);
    }
  };

  const handleConnect = () => {
    if (selectedBusinessId) {
      mutateConnect(selectedBusinessId);
    }
  };

  return (
    <>
      {isMobile && <MobileHeader href="/profile" title="RED Statement" />}
      {!isMobile && (
        <div className="max-w-[552px] px-14 pt-12 text-400 font-bold text-black-100">
          RED Statement
        </div>
      )}
      {!isInitiated ? (
        <div className="relative h-40 max-w-96">
          <Loader />
        </div>
      ) : (
        <div className="px-4 md:max-w-[552px] md:px-14">
          <div className="pt-10 text-center text-black-54 md:pt-2 md:text-left">
            {!redConnectedBusinessId ? (
              <>
                FBTC Accountancy Services can connect to your RDS Driving
                Services Ltd account and import your transactions directly in
                our app. To do this we need your permission to access this
                information, by clicking "Connect" you are authorising us to
                connect to your account and import this data.
                <br />
                <br />
                FBTC Accountancy Services will not review any of the
                transactions provided by RDS Driving Services Ltd until we
                prepare your quarterly submission or annual return, it is
                therefore your responsibility to make sure that all transactions
                entered are correct. Neither FBTC Accountancy Services nor any
                member of its staff will be responsible for any financial loss
                or damage or consequential financial loss or damage resulting
                from any party placing reliance on the information supplied.
              </>
            ) : (
              <>
                {`Your account is currently linked to RDS Driving Service Ltd. as ${clientCode} → ${businesses.find(({ uniqueId }) => uniqueId === redConnectedBusinessId)?.name ?? redConnectedBusinessId}`}
                <br />
                <br />
                If you would like to assign future RDS Driving Service Ltd.
                transactions to a different business you can do so by clicking
                the "Change Business" button below.
                <br />
                <br />
                If you would like to stop future RDS Driving Service Ltd.
                transactions automatically uploading to you account you can
                click the "Disconnect" button below.
              </>
            )}
          </div>
          {!redConnectedBusinessId && (
            <div className="pt-10">
              <Button
                title="Connect RED Statement"
                className="mx-auto w-full max-w-56 md:mx-0"
                onClick={handleOpenModal}
              />
            </div>
          )}
          {redConnectedBusinessId && (
            <div className="flex items-center justify-between pt-10">
              <Button
                title="Change Business"
                className="w-full max-w-56"
                onClick={handleOpenModal}
              />
              <Button
                title="Disconnect"
                variant="text"
                onClick={() => setShowDisconnectModal(true)}
                disabled={isDisconnecting}
                className="text-black-54"
              />
            </div>
          )}
        </div>
      )}
      <ConfirmModal
        show={showConnectModal}
        confirmButtonTitle="Connect"
        cancelButtonTitle="Cancel"
        onClickCancel={() => {
          setShowConnectModal(false);
          setSelectedBusinessId(redConnectedBusinessId || "");
        }}
        onClickConfirm={handleConnect}
        disabledConfirmButton={!selectedBusinessId || isConnecting}
      >
        <div className="flex flex-col items-center gap-4">
          <div className="text-400 font-bold text-black-100">
            Connect RED Statement
          </div>
          <div className="px-4 text-100 text-black-54 md:text-300 md:font-light md:text-black-70">
            Your RED Statement will be imported into FBTC as transactions
            against the selected business below. You are responsible for
            reviewing any transactions created and can choose to disconnect at
            any time.
          </div>
          <div className="w-full px-4">
            <BusinessSelector
              value={selectedBusinessId}
              onSetBusiness={setSelectedBusinessId}
            />
          </div>
        </div>
      </ConfirmModal>
      <ConfirmModal
        show={showDisconnectModal}
        confirmButtonTitle="Disconnect"
        cancelButtonTitle="Cancel"
        onClickCancel={() => setShowDisconnectModal(false)}
        onClickConfirm={() => {
          mutateDisconnect();
          setShowDisconnectModal(false);
        }}
        disabledConfirmButton={isDisconnecting}
      >
        <div className="flex flex-col items-center gap-4">
          <div className="text-400 font-bold text-black-100">
            Disconnect RED Statement
          </div>
          <div className="px-4 text-100 text-black-54 md:text-300 md:font-light md:text-black-70">
            Are you sure you want to disconnect your RED Statement?
          </div>
        </div>
      </ConfirmModal>
      <Modal
        show={showNoClientCodeModal}
        onClose={() => setShowNoClientCodeModal(false)}
      >
        <div className="flex flex-col items-center text-center">
          <div className="text-400 font-bold text-black-100">
            Client Code Required
          </div>
          <div className="pt-2 text-100 text-black-54 md:text-300 md:font-light md:text-black-70">
            Your account doesn't have a client code yet. Please contact your
            accountant.
          </div>
        </div>
        <div className="flex justify-between gap-x-2 pt-8 md:gap-x-8 md:pt-12">
          <Button
            type="button"
            variant="primary"
            title="Ok"
            className="mx-auto w-40"
            onClick={() => setShowNoClientCodeModal(false)}
          />
        </div>
      </Modal>
    </>
  );
};
